import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Modal, Button, Tag, EntityAvatar, Tooltip, Spin, SectionHead, SECTION_TINT, cn, type TagTone,
} from '../../ui';
import {
  Truck, PackageCheck, Package, Boxes, CheckCheck, Ban, AlertTriangle, ArrowRight, ClipboardList,
  ShoppingCart, Repeat, CalendarClock, FileText, ScrollText, CircleCheck, CircleDashed, Scale,
} from 'lucide-react';
import { request } from '../../api/client';
import { productImage } from '../../lib/imageUrls';
import { ProductThumb } from '../../components/ProductThumb';
import { WarehouseCode, MasterTag } from '../../lib/warehouseLabel';
import { CopyValue } from '../../components/CopyValue';
import { ExpiryChip } from '../../components/ExpiryChip';
import { RoleBadge } from '../../components/RoleBadge';
import { OrderLink, RetailerLink, UserLink } from '../../components/EntityLinks';
import { formatIst, formatIstDate } from '../../lib/time';
import { fmtQty, isRedundantSku } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { waitingDays, waitingLabel, ageBucket, type AgeBucket } from '../../lib/waiting';
import { stateName } from '../../constants/indianStates';

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE STORY OF ONE STOCK TRANSFER.
 *
 * The register used to answer a row-click by unrolling a strip of product / batch / qty underneath
 * it. That is the LEAST interesting thing about a transfer: the row already says how many items and
 * how much, and the strip pushed every other row down the page to repeat it. What a warehouse
 * manager actually asks of a movement is none of that —
 *
 *     WHY does this transfer exist?      an order was short, or a depot is being rebalanced
 *     WHO moved it, and WHEN?            requested / acknowledged / prepared / dispatched / received
 *     DID IT ARRIVE INTACT?              dispatched vs received, short and damaged, named
 *     WHERE IS THE PROOF?                the receipt GRN and the ledger entries behind it
 *
 * — and none of it was reachable from the register at all. So the row now opens THIS, and the four
 * questions above are the four bands of the dialog, in that order.
 *
 * READ-ONLY BY DESIGN. Dispatch / receive / cancel / settle stay on the register row where they have
 * always been (and where each is gated by its own permission and warehouse scope). A dialog that
 * both explains a movement and mutates it invites acting on a half-read screen; this one exists so
 * the reader can decide, and the decision is taken on the row.
 * ──────────────────────────────────────────────────────────────────────────────────────────────── */

const STATUS_TONE: Record<string, TagTone> = {
  DRAFT: 'neutral', REQUESTED: 'blue', SOURCE_ACK: 'cyan', PREPARED: 'blue',
  IN_TRANSIT: 'gold', RECEIVED: 'green', PARTIAL: 'red', CANCELLED: 'red', REJECTED: 'red',
};
const AGE_TAG: Record<AgeBucket, TagTone> = { fresh: 'neutral', day: 'gold', ageing: 'gold', stale: 'red' };
const human = (s?: string | null) => (s ?? '').replace(/_/g, ' ');

/**
 * A person and the moment they acted, as ONE unit.
 *
 * A name with a timestamp under it in grey reads as two facts the reader has to pair up; and a
 * transfer's whole point here is that each step has an owner. Name in full contrast, their role as
 * its own coloured pill beside it (the app's rule for "role beside a name"), time quietly beneath.
 * A stamp whose user has since been deleted still prints the time — the act happened.
 */
function Actor({ act, fallback = 'Not recorded' }: { act?: any; fallback?: string }) {
  if (!act) return <span className="type-body text-subtle">{fallback}</span>;
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="truncate type-body-strong text-content">
          {act.name ? <UserLink id={act.userId} role={act.role}>{act.name}</UserLink> : <span className="text-subtle">System</span>}
        </span>
        {act.role && <RoleBadge role={act.role} />}
      </div>
      {act.at && <div className="mt-0.5 type-caption text-subtle">{formatIst(act.at)}</div>}
    </div>
  );
}

/** A labelled fact. SECTION / label / **value** are three visibly different levels — see SectionHead. */
function F({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-1 min-w-0 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

/** A group of facts under its own coloured mark. */
function Group({ icon, tint, title, hint, children, className }: {
  icon: ReactNode; tint: string; title: string; hint?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={className}>
      <SectionHead icon={icon} tint={tint} hint={hint}>{title}</SectionHead>
      {children}
    </section>
  );
}

/** One end of the journey — the code chip people say out loud, then the site it names. */
function WhBlock({ wh, id, label, icon }: { wh: any; id: any; label: string; icon: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 type-overline text-subtle">{icon}{label}</div>
      <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
        {wh?.code ? <WarehouseCode code={wh.code} /> : null}
        <span className="truncate type-body-strong text-content">{wh?.name ?? `#${id ?? '—'}`}</span>
        {wh?.isMaster ? <MasterTag /> : null}
        {wh && wh.isActive === false && <Tag tone="neutral">retired</Tag>}
      </div>
      {/* `stateCode` is the GST numeric code (27, 29, …), which is a tax key and not a place — printed
          raw it read "Mumbai, 27". Resolved to the state's name, the app's one rule for that field. */}
      {wh?.city && <div className="mt-0.5 type-caption text-subtle">{[wh.city, stateName(wh.stateCode)].filter(Boolean).join(', ')}</div>}
    </div>
  );
}

/**
 * A quantity tile. Not a `StatCard` — these four are one ARITHMETIC (dispatched = received + damaged
 * + short), so they read as a strip of related figures rather than four independent KPIs, and a tile
 * that is zero has to stay visibly zero instead of being dropped.
 */
function QtyTile({ label, value, tone, icon, hint, muted }: {
  label: string; value: ReactNode; tone: 'brand' | 'gold' | 'danger' | 'slate'; icon: ReactNode; hint?: ReactNode; muted?: boolean;
}) {
  const tones = {
    brand: 'border-brand-500/25 bg-brand-500/[0.07] text-brand-700 dark:text-brand-300',
    gold: 'border-harvest-400/35 bg-harvest-400/[0.10] text-harvest-700 dark:text-harvest-300',
    danger: 'border-danger-500/25 bg-danger-500/[0.08] text-danger-600 dark:text-danger-400',
    slate: 'border-line bg-surface-2 text-content',
  };
  return (
    <div className={cn('rounded-card border p-3', tones[tone], muted && 'opacity-55')}>
      <div className="flex items-center gap-1.5 type-overline opacity-90">{icon}{label}</div>
      <div className="mt-1 type-metric-sm text-content">{value}</div>
      {hint && <div className="mt-0.5 type-caption text-subtle">{hint}</div>}
    </div>
  );
}

/**
 * THE JOURNEY, as the acts that actually happened.
 *
 * Deliberately not a fixed five-step tracker. Acknowledge and prepare are OPTIONAL courtesies — a
 * depot that just loads the van dispatches straight from REQUESTED — so drawing them as permanently
 * "skipped" steps would accuse every such depot of missing a step it was never required to take.
 * They appear when they happened and are quietly absent when they did not.
 *
 * A consolidation transfer collapses request and dispatch into ONE act, because that is literally
 * what the server does: `createTransfer` stamps dispatchedBy/At at creation for an order-linked
 * transfer, so the same person at the same instant did both. Two rows would invent a step.
 */
function Journey({ t }: { t: any }) {
  const a = t.acts ?? {};
  const consolidated = t.origin?.kind === 'ORDER_SHORTFALL';
  const sameMoment = consolidated && a.dispatched?.at && a.requested?.at
    && Math.abs(new Date(a.dispatched.at).getTime() - new Date(a.requested.at).getTime()) < 5000;

  const steps: Array<{ key: string; title: string; note?: string; icon: ReactNode; act: any; tone: 'brand' | 'gold' | 'danger' }> = [];
  steps.push({
    key: 'requested',
    title: sameMoment ? 'Raised & dispatched' : 'Raised',
    note: sameMoment
      ? 'A consolidation transfer leaves the source the moment it is raised — there is no separate dispatch step.'
      : consolidated ? 'Raised automatically to cover an order shortfall.' : undefined,
    icon: <ClipboardList className="size-3.5" />, act: a.requested, tone: 'brand',
  });
  if (a.acknowledged) steps.push({ key: 'acknowledged', title: 'Acknowledged by the source', note: 'The source depot confirmed it has the stock.', icon: <CheckCheck className="size-3.5" />, act: a.acknowledged, tone: 'brand' });
  if (a.prepared) steps.push({ key: 'prepared', title: 'Picked & packed', note: 'Goods pulled from the shelf and waiting for a vehicle.', icon: <Boxes className="size-3.5" />, act: a.prepared, tone: 'brand' });
  if (a.dispatched && !sameMoment) steps.push({ key: 'dispatched', title: 'Dispatched', note: 'Stock left the source warehouse and is in transit.', icon: <Truck className="size-3.5" />, act: a.dispatched, tone: 'brand' });
  if (a.received) {
    steps.push({
      key: 'received', title: 'Received at the destination',
      note: t.reconciliation?.shortQty || t.reconciliation?.damagedQty
        ? 'Counted in with a difference — see Dispatched vs received below.'
        : 'Counted in full and back on the shelf.',
      icon: <PackageCheck className="size-3.5" />, act: a.received,
      tone: (t.reconciliation?.shortQty || t.reconciliation?.damagedQty) ? 'gold' : 'brand',
    });
  }
  /* THE CANCELLATION, as up to three separate acts — asked, decided, and (if allowed) done.
     They are genuinely different moments by different people: a warehouse manager asks, a Super
     Admin rules, and the ruling is what performs the cancellation. Collapsing them into one
     "Cancelled by <the Super Admin>" would credit the decision to the wrong person and lose the
     reason the asker gave, which is the sentence everyone downstream actually reads. */
  const cr = t.cancelRequest;
  if (cr) {
    steps.push({
      key: 'cancel-asked', title: 'Cancellation requested',
      note: `“${cr.reason}”`,
      icon: <Ban className="size-3.5" />, act: cr.requested,
      tone: cr.status === 'PENDING' ? 'gold' : 'danger',
    });
    if (cr.status === 'REJECTED') {
      steps.push({
        key: 'cancel-refused', title: 'Cancellation refused — the transfer stands',
        note: cr.decisionNote ? `“${cr.decisionNote}”` : 'The freeze lifted and the transfer carried on.',
        icon: <CircleCheck className="size-3.5" />, act: cr.decided, tone: 'brand',
      });
    }
  }
  if (a.cancelled) {
    steps.push({
      key: 'cancelled', title: cr?.status === 'APPROVED' ? 'Cancellation approved — transfer cancelled' : 'Cancelled',
      // The reason on the transfer is the REQUESTER's when a request was approved, which is why the
      // record reads correctly whichever route it took.
      note: a.cancelled.reason
        ? `“${a.cancelled.reason}”`
        : t.status === 'REJECTED' ? 'Rejected before it moved.' : 'No reason was recorded (cancelled before reasons were required).',
      icon: <Ban className="size-3.5" />, act: a.cancelled, tone: 'danger',
    });
  }

  // What has NOT happened yet, named once at the end rather than as a row of grey placeholders.
  const open = !['RECEIVED', 'CANCELLED', 'REJECTED'].includes(t.status);
  // A pending cancellation OUTRANKS the ordinary next step: the transfer is frozen, so the source is
  // not in fact waiting to dispatch — nobody may.
  const awaiting = cr?.status === 'PENDING'
    ? 'Waiting on a Super Admin to decide the cancellation — the transfer is frozen until then'
    : t.status === 'PARTIAL'
      ? 'Waiting on an admin to settle the difference'
      : ['REQUESTED', 'SOURCE_ACK', 'PREPARED', 'DRAFT'].includes(t.status)
        ? `Waiting on ${t.fromWarehouse?.code ?? 'the source'} to dispatch`
        : t.status === 'IN_TRANSIT'
          ? `Waiting on ${t.toWarehouse?.code ?? 'the destination'} to receive`
          : null;

  const dotTone = { brand: 'bg-brand-600 text-white', gold: 'bg-harvest-400 text-harvest-900', danger: 'bg-danger-500 text-white' };

  return (
    <ol className="relative space-y-0">
      {steps.map((s, i) => (
        <li key={s.key} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className={cn('grid size-7 shrink-0 place-items-center rounded-full shadow-e1', dotTone[s.tone])}>{s.icon}</span>
            {(i < steps.length - 1 || (open && awaiting)) && <span className="my-1 w-px flex-1 bg-line" />}
          </div>
          <div className="min-w-0 flex-1 pb-4">
            <div className="type-body-strong text-content">{s.title}</div>
            <div className="mt-1.5"><Actor act={s.act} /></div>
            {s.note && <div className="mt-1 type-caption text-subtle">{s.note}</div>}
          </div>
        </li>
      ))}
      {open && awaiting && (
        <li className="flex gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-full border border-dashed border-line-strong text-subtle">
            <CircleDashed className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="type-body-strong text-subtle">{awaiting}</div>
            {t.waitingSince != null && waitingDays(t.waitingSince) != null && (
              <div className="mt-1">
                <Tooltip label="Counted from the step this transfer is waiting on — not from the day it was raised">
                  <Tag tone={AGE_TAG[ageBucket(waitingDays(t.waitingSince) as number)]}>
                    waiting {waitingLabel(waitingDays(t.waitingSince) as number)}
                  </Tag>
                </Tooltip>
              </div>
            )}
          </div>
        </li>
      )}
    </ol>
  );
}

/** A signed ledger quantity — the sign IS the meaning, so it is never dropped. */
function LedgerQty({ qty }: { qty: number }) {
  const out = qty < 0;
  return (
    <span className={cn('type-body-strong tabular-nums', out ? 'text-danger-600 dark:text-danger-400' : 'text-brand-700 dark:text-brand-400')}>
      {out ? '−' : '+'}{fmtQty(Math.abs(qty))}
    </span>
  );
}

const LEDGER_TONE: Record<string, TagTone> = { TRANSFER_OUT: 'red', TRANSFER_IN: 'green', GRN: 'green' };

/**
 * WHAT A LEDGER ENTRY IS, not merely which txn type it carries.
 *
 * Asked of a transfer that arrived one unit short: *"if 1 short then how is it transferred in again to
 * the source — is it a different product?"* No — same product, same batch. The +1 is not a second
 * movement of goods, it is the SETTLEMENT of the shortfall: an admin ruled "returned to source", which
 * means that unit never actually left, so it goes back on the source's books. The alternative ruling,
 * "accept loss", posts nothing at all — the source lost it at dispatch and the loss already stands.
 *
 * The row could not say any of that. `TRANSFER IN +1` sat under `TRANSFER OUT −3` looking like the
 * goods had bounced back, and the reader had to join it to the decision three sections above by
 * themselves. What distinguishes the two is the ref the entry was posted against, which is why the
 * detail payload ships `refTable` — the txn type alone genuinely cannot tell them apart.
 */
function ledgerMeaning(m: any): string {
  if (m.refTable === 'transfer_discrepancies') {
    return 'Difference settled — returned to the source, so the shortfall goes back on its books';
  }
  if (m.refTable === 'grn_headers') return 'Counted in at the destination, through the receipt';
  if (m.txnType === 'TRANSFER_OUT') return 'Left the source on dispatch';
  if (m.txnType === 'TRANSFER_IN') return 'Transfer cancelled — credited back to the source';
  return '';
}

export function TransferDetailModal({ transferId, fallbackRow, prodMap, open, onClose, onReceive }: {
  transferId: string | null;
  /** The register row, so the dialog paints its identity instantly instead of flashing an empty frame. */
  fallbackRow?: any;
  /** Catalogue rows keyed by product id — passed in so a line can show its own pack picture without
   *  this dialog fetching a second copy of the product list the register already holds. */
  prodMap?: Map<string, any>;
  open: boolean;
  onClose: () => void;
  /**
   * Book the consignment in. Passed only when this reader may receive it — the register decides, by the
   * same rule its row button uses — and still hidden once the record in hand is no longer in transit, so
   * a transfer received elsewhere in the meantime never offers it.
   */
  onReceive?: () => void;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['stock-transfer-detail', transferId],
    queryFn: () => request<any>({ url: `/stock-transfers/${transferId}/detail` }),
    enabled: open && !!transferId,
  });

  // Everything below reads `t`. Before the fetch lands that is the register row, which carries the
  // header facts (number, route, status, lines) but none of the joined ones — so each block below
  // renders only from what it is given and never assumes the detail half is there.
  const t = (data ?? fallbackRow ?? {}) as any;
  const lines: any[] = t.lines ?? [];
  const rec = t.reconciliation;
  const discrepancies: any[] = t.discrepancies ?? [];
  const movements: any[] = t.movements ?? [];
  const openDiffs = discrepancies.filter((d) => d.status === 'OPEN').length;
  /* The cancellation ask, whatever became of it. The register row carries only the pending one
     (`cancelRequest` with a reason); the detail payload carries the full record including a decided
     one, so the pending band below works from either and the journey works from the richer half. */
  const cancelReq = t.cancelRequest ?? null;

  // A line's own dispatched-vs-received split, only once the destination has actually counted.
  const anyCounted = useMemo(() => lines.some((l) => l.receivedQty != null), [lines]);

  /**
   * The lines grouped by PRODUCT, in the order they first appear.
   *
   * A transfer line is one lot of one product, so a product picked from three batches is three
   * lines — correct in the data, and misread on screen as three products. Grouping lets the table
   * name the product once and stack its lots beside it. `total` is the product's own quantity across
   * its lots, which is the figure a reader wants when a product spans several.
   */
  /** How many lines still name no batch — the source has not picked them yet. */
  const unpickedLines = useMemo(() => lines.filter((l) => l.batchId == null).length, [lines]);

  const itemGroups = useMemo(() => {
    const m = new Map<string, { productId: string; productName?: string; sku?: string; rows: any[]; total: number }>();
    for (const l of lines) {
      const k = String(l.productId);
      const g = m.get(k) ?? { productId: k, productName: l.productName, sku: l.sku, rows: [] as any[], total: 0 };
      g.rows.push(l);
      g.total += Number(l.qty) || 0;
      m.set(k, g);
    }
    return [...m.values()];
  }, [lines]);

  /**
   * THE SAME PRODUCT, MOVED FROM THREE LOTS, IS ONE MOVEMENT OF ONE PRODUCT.
   *
   * The ledger posts one row per BATCH, so dispatching a product drawn from three lots wrote
   * "AGN 0.5L" three times down the middle of the table with everything either side of it — the
   * time, the movement, the warehouse, the person — repeated identically. Three near-identical rows
   * read as three different products until you compare the names character by character, which is
   * exactly the misreading the items table above was already fixed for.
   *
   * The key is every column the group would SPAN — displayed time, movement, warehouse, product,
   * actor. Rows only merge when all of those are literally the same text, so a spanning cell can
   * never claim something untrue of one of its lots: a credit-back posted a day later, or by someone
   * else, or into the other warehouse, splits into its own group by construction.
   */
  const movementGroups = useMemo(() => {
    const m = new Map<string, { key: string; head: any; when: string; rows: any[]; total: number }>();
    for (const mv of movements) {
      const when = formatIst(mv.at);
      const key = [mv.txnType, String(mv.warehouseId ?? ''), String(mv.productId ?? ''), mv.actorName ?? '', when].join('|');
      const g = m.get(key) ?? { key, head: mv, when, rows: [] as any[], total: 0 };
      g.rows.push(mv);
      g.total += Number(mv.qty) || 0;
      m.set(key, g);
    }
    return [...m.values()];
  }, [movements]);

  const errMsg = (error as any)?.response?.data?.error?.message;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Transfer details"
      width={1240}
      footer={<>
        {onReceive && t.status === 'IN_TRANSIT' && (
          <Button variant="primary" icon={<PackageCheck size={16} />} onClick={onReceive}>Receive transfer</Button>
        )}
        <Button variant="default" onClick={onClose}>Close</Button>
      </>}
    >
      {isError ? (
        <div className="rounded-card border border-danger-500/30 bg-danger-500/[0.06] p-4 type-body text-content">
          {errMsg ?? 'This transfer could not be loaded.'}
        </div>
      ) : (
        <div className="space-y-4">
          {/* ── LETTERHEAD ── who this document is, where it goes, and what state it is in. The route
              is the headline because a transfer IS a journey; the number is the thing people quote. */}
          <div className="rounded-card bg-gradient-to-r from-brand-600/10 via-brand-500/5 to-transparent p-3.5 ring-1 ring-inset ring-brand-600/10">
            <div className="flex flex-wrap items-start gap-3">
              <span className="grid size-12 shrink-0 place-items-center rounded-card bg-brand-600 text-white shadow-e1">
                <Truck className="size-6" />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <CopyValue value={t.transferNo} label="Transfer no">
                    <span className="type-section-title text-content">{t.transferNo ?? '—'}</span>
                  </CopyValue>
                  <Tag tone={STATUS_TONE[t.status] ?? 'neutral'}>{human(t.status)}</Tag>
                  {openDiffs > 0 && (
                    <Tag tone="red" icon={<AlertTriangle className="size-3" />}>
                      {openDiffs} open {openDiffs === 1 ? 'difference' : 'differences'}
                    </Tag>
                  )}
                </div>
                <div className="mt-1 type-caption text-subtle">
                  Raised {formatIstDate(t.transferDate)}
                  {t.receivedAt ? ` · closed ${formatIstDate(t.receivedAt)}` : ''}
                </div>
              </div>
              {t.receipt?.grnNo && (
                <div className="ml-auto text-right">
                  <div className="type-overline text-subtle">Goods receipt</div>
                  <div className="mt-1">
                    <CopyValue value={t.receipt.grnNo} label="GRN no">
                      <Tag tone="green" icon={<FileText className="size-3" />}>{t.receipt.grnNo}</Tag>
                    </CopyValue>
                  </div>
                  <div className="mt-0.5 type-caption text-subtle">{formatIstDate(t.receipt.grnDate)}</div>
                </div>
              )}
            </div>

            {/* The journey itself, as two named ends rather than "#2 → #7". */}
            <div className="mt-3 grid grid-cols-1 items-center gap-3 rounded-control border border-line/70 bg-surface/70 p-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              <WhBlock wh={t.fromWarehouse} id={t.fromWarehouseId} label="From" icon={<Package className="size-3" />} />
              <ArrowRight className="mx-auto size-5 shrink-0 rotate-90 text-brand-600 sm:rotate-0 dark:text-brand-400" />
              <WhBlock wh={t.toWarehouse} id={t.toWarehouseId} label="To" icon={<PackageCheck className="size-3" />} />
            </div>
          </div>

          {isLoading && !data && (
            <div className="flex items-center justify-center gap-2 py-6 type-body text-subtle"><Spin /> Loading the full record…</div>
          )}

          {/* ── A CANCELLATION SOMEBODY ASKED FOR ──
              First, above everything else, when it is still PENDING: the transfer is FROZEN and no
              other fact on this screen matters until that is decided. A decided one stays on the
              record lower down (in the journey), because who asked and who allowed it is exactly
              what the request exists to preserve. */}
          {cancelReq?.status === 'PENDING' && (
            <div className="rounded-card border border-danger-500/35 bg-danger-500/[0.07] p-3.5">
              <div className="flex flex-wrap items-center gap-2">
                <Ban className="size-4 text-danger-600 dark:text-danger-400" />
                <span className="type-body-strong text-content">Cancellation requested — waiting on a Super Admin</span>
                <Tag tone="red">frozen</Tag>
              </div>
              <p className="mt-1 type-caption text-subtle">
                Nothing can move on this transfer while the request is open: it cannot be acknowledged, prepared,
                dispatched or received. If the Super Admin refuses it, the freeze lifts and the transfer carries on.
              </p>
              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <F label="Asked by"><Actor act={cancelReq.requested} /></F>
                <F label="Status when asked">{human(cancelReq.transferStatus) || undefined}</F>
                <F label="Reason given" className="sm:col-span-2">
                  <span className="whitespace-pre-wrap font-normal">{cancelReq.reason}</span>
                </F>
              </dl>
            </div>
          )}

          {/* ── WHY THIS TRANSFER EXISTS ── the question the register could never answer. Two kinds,
              two colours, and the order (when there is one) named with its customer beside it. */}
          {data && (
            <Group icon={t.origin?.kind === 'ORDER_SHORTFALL' ? <ShoppingCart className="size-3.5" /> : <Repeat className="size-3.5" />}
              tint={t.origin?.kind === 'ORDER_SHORTFALL' ? SECTION_TINT.money : SECTION_TINT.place}
              title="Why this transfer exists">
              {t.origin?.kind === 'ORDER_SHORTFALL' ? (
                <div className="rounded-card border border-harvest-400/40 bg-harvest-400/[0.08] p-3.5">
                  <div className="type-body-strong text-content">
                    Raised to cover a shortfall on a customer order.
                  </div>
                  <p className="mt-1 type-caption text-subtle">
                    {t.toWarehouse?.name ?? 'The dispatch warehouse'} could not cover the order on its own, so this
                    transfer gathers the missing stock from {t.fromWarehouse?.name ?? 'another warehouse'}. The order is
                    held until every transfer raised for it has landed.
                  </p>
                  {t.origin.order ? (
                    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                      <F label="Order">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <OrderLink id={t.origin.order.id}>{t.origin.order.orderNo}</OrderLink>
                          <Tag tone={t.origin.order.stillHeld ? 'gold' : 'neutral'}>{human(t.origin.order.status)}</Tag>
                        </div>
                      </F>
                      <F label="Customer">
                        {t.origin.order.retailerName
                          ? <RetailerLink id={t.origin.order.retailerId}>{t.origin.order.retailerName}</RetailerLink>
                          : undefined}
                      </F>
                      <F label="Order raised">{t.origin.order.orderDate ? formatIstDate(t.origin.order.orderDate) : undefined}</F>
                      <F label="Order placed by">
                        {t.origin.order.raisedBy?.name
                          ? <UserLink id={t.origin.order.raisedBy.userId}>{t.origin.order.raisedBy.name}</UserLink>
                          : undefined}
                      </F>
                      <F label="Sales person">
                        {t.origin.order.salesPerson?.name
                          ? <UserLink id={t.origin.order.salesPerson.userId}>{t.origin.order.salesPerson.name}</UserLink>
                          : undefined}
                      </F>
                      <F label="Order status now">
                        {t.origin.order.stillHeld
                          ? <span className="text-harvest-700 dark:text-harvest-300">Held — awaiting transfer</span>
                          : <span className="text-brand-700 dark:text-brand-400">Released</span>}
                      </F>
                    </dl>
                  ) : (
                    <p className="mt-2 type-caption text-subtle">The order behind this transfer is no longer on record.</p>
                  )}
                </div>
              ) : (
                <div className="rounded-card border border-sky-500/30 bg-sky-500/[0.06] p-3.5">
                  <div className="type-body-strong text-content">Stock rebalancing — no order behind it.</div>
                  <p className="mt-1 type-caption text-subtle">
                    Raised by a manager to top {t.toWarehouse?.name ?? 'the destination'} up from{' '}
                    {t.fromWarehouse?.name ?? 'the source'}. Nothing is waiting on it downstream, and the stock only
                    leaves the source when it is dispatched.
                  </p>
                </div>
              )}
              {t.remarks && (
                <div className="mt-2.5 rounded-control border border-line bg-surface-2 p-2.5">
                  <div className="type-overline text-subtle">Remark</div>
                  <div className="mt-1 whitespace-pre-wrap type-body text-content">{t.remarks}</div>
                </div>
              )}
            </Group>
          )}

          {/* ── DISPATCHED vs RECEIVED ── the arithmetic the destination is judged on. Null until the
              destination counts: before that nothing is short, it is in transit, and saying otherwise
              would mark every healthy transfer as 100% short for its whole journey. */}
          {data && rec && (
            <Group icon={<Scale className="size-3.5" />} tint={SECTION_TINT.assignment} title="Dispatched vs received"
              hint={rec.counted ? undefined : 'not counted yet'}>
              <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                <QtyTile label="Dispatched" value={fmtQty(rec.dispatchedQty)} tone="slate" icon={<Truck className="size-3" />}
                  /* "lots" once every line names one, "lines" while the source has yet to pick —
                     the same word the Items table uses, so the tile and the table agree. */
                  hint={unpickedLines > 0
                    ? `${lines.length} ${lines.length === 1 ? 'product' : 'products'}`
                    : `${lines.length} ${lines.length === 1 ? 'lot' : 'lots'}`} />
                <QtyTile label="Received good" value={rec.counted ? fmtQty(rec.receivedQty) : '—'} tone="brand"
                  icon={<CircleCheck className="size-3" />} muted={!rec.counted}
                  hint={rec.counted ? 'back on the shelf' : 'awaiting receipt'} />
                <QtyTile label="Damaged" value={rec.counted ? fmtQty(rec.damagedQty) : '—'} tone="gold"
                  icon={<AlertTriangle className="size-3" />} muted={!rec.counted || !rec.damagedQty}
                  hint={rec.counted ? 'arrived unsellable' : 'awaiting receipt'} />
                <QtyTile label="Short" value={rec.counted ? fmtQty(rec.shortQty) : '—'} tone="danger"
                  icon={<AlertTriangle className="size-3" />} muted={!rec.counted || !rec.shortQty}
                  hint={rec.counted ? 'never arrived' : 'awaiting receipt'} />
              </div>
              <p className="mt-2 type-caption text-subtle">
                {!rec.counted
                  ? t.status === 'CANCELLED' || t.status === 'REJECTED'
                    ? 'This transfer was called off before the destination counted it in. Any stock already in transit was credited back to the source.'
                    : 'The destination has not counted this consignment in yet, so nothing is short — it is still on the move.'
                  : rec.shortQty || rec.damagedQty
                    ? `${fmtQty((rec.shortQty ?? 0) + (rec.damagedQty ?? 0))} of ${fmtQty(rec.dispatchedQty)} did not make it onto the destination's shelf.${openDiffs ? ' An admin still has to decide whether that is written off or credited back to the source.' : ' Settled below.'}`
                    : 'Everything that left the source arrived and was counted in. No difference to settle.'}
              </p>
            </Group>
          )}

          {/* ── THE JOURNEY ── every act, its owner, and the moment it happened. */}
          {data && (
            <Group icon={<Truck className="size-3.5" />} tint={SECTION_TINT.identity} title="Who moved it, and when">
              <div className="rounded-card border border-line bg-surface p-3.5 shadow-e1">
                <Journey t={t} />
              </div>
            </Group>
          )}

          {/* ── WHAT MOVED ── the line detail the old expanded strip carried, plus the half it never
              did: what each line was supposed to be against what the destination actually counted. */}
          <Group icon={<Boxes className="size-3.5" />} tint={SECTION_TINT.farm} title="Items on this transfer"
            hint={lines.length
              ? `${itemGroups.length} ${itemGroups.length === 1 ? 'product' : 'products'}${lines.length !== itemGroups.length ? ` · ${lines.length} lots` : ''}`
              : undefined}>
            {lines.length === 0 ? (
              <div className="rounded-card border border-line bg-surface-2 p-4 type-body text-subtle">
                This transfer carries no lines.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-card border border-line">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr className="bg-surface-2 text-left">
                      <th className="px-3 py-2 type-overline text-subtle">#</th>
                      <th className="px-3 py-2 type-overline text-subtle">Product</th>
                      <th className="px-3 py-2 type-overline text-subtle">Batch</th>
                      <th className="px-3 py-2 text-right type-overline text-subtle">Dispatched</th>
                      {anyCounted && <th className="px-3 py-2 text-right type-overline text-subtle">Received</th>}
                      {anyCounted && <th className="px-3 py-2 text-right type-overline text-subtle">Damaged</th>}
                      {anyCounted && <th className="px-3 py-2 text-right type-overline text-subtle">Short</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {/*
                      ONE PRODUCT, ITS LOTS STACKED BESIDE IT.
                      A transfer of one product drawn from three batches is ONE product and three
                      lots — but a flat row per line printed "AGN 0.5L" three times down the middle
                      of the table, and the repetition read as three different products until you
                      compared the names character by character. A spanning cell says the shape the
                      data actually has: the batch is a property of a line, not a thing being moved.
                      The `#` therefore numbers PRODUCTS, which is what the transfer is a list of.
                    */}
                    {itemGroups.map((g, gi) => g.rows.map((l: any, ri: number) => {
                      // `goodQty` / `shortQty` only exist on the detail payload; the register row has
                      // the raw columns, so fall back to those and treat NULL as "not counted".
                      const good = l.goodQty ?? (l.receivedQty == null ? null : Number(l.receivedQty));
                      const damaged = l.damagedQty == null ? null : Number(l.damagedQty);
                      const short = l.shortQty != null
                        ? Number(l.shortQty)
                        : (good == null && damaged == null ? null : Math.max(0, Number(l.qty) - (good ?? 0) - (damaged ?? 0)));
                      const problem = (short ?? 0) > 0 || (damaged ?? 0) > 0;
                      const phrase = boxLoosePhrase(l.boxQuantity, l.looseUnitQuantity, l.unitsPerBoxSnapshot);
                      return (
                        <tr key={String(l.id ?? `${gi}:${ri}`)} className={cn(
                          // A hairline between lots of one product; a heavier rule where the next
                          // product starts, so the groups separate without a second colour.
                          ri > 0 && 'border-t border-line/70',
                          ri === 0 && gi > 0 && 'border-t-2 border-line-strong',
                          ri === 0 && gi === 0 && 'border-t border-line/70',
                          problem && 'bg-danger-500/[0.045]',
                        )}>
                          {ri === 0 && (
                            <td rowSpan={g.rows.length} className="px-3 py-2.5 align-middle type-caption text-subtle tabular-nums">{gi + 1}</td>
                          )}
                          {ri === 0 && (
                            <td rowSpan={g.rows.length} className="border-r border-line bg-surface-2/40 px-3 py-2.5 align-middle">
                              <div className="flex min-w-0 items-center gap-2.5">
                                <ProductThumb name={g.productName ?? String(g.productId)} src={productImage(prodMap?.get(String(g.productId)))} size={32} />
                                <div className="min-w-0 leading-tight">
                                  <div className="truncate type-label-strong text-content">{g.productName ?? `#${g.productId}`}</div>
                                  {g.sku && !isRedundantSku(g.sku, g.productName) && (
                                    <div className="truncate type-caption text-subtle">{g.sku}</div>
                                  )}
                                </div>
                              </div>
                              {/* The per-product subtotal earns its place only when there is more than
                                  one lot to add up. On a single-lot product it is the row's own figure
                                  printed again, a few pixels to the left. */}
                              {g.rows.length > 1 && (
                                <div className="mt-1.5 type-caption text-subtle">
                                  {g.rows.length} lots · <b className="text-content">{fmtQty(g.total)}</b> units
                                </div>
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2.5 align-middle">
                            {l.batchNo ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <CopyValue value={l.batchNo} label="Batch">
                                  <Tag tone="neutral" icon={<Package className="size-3" />} style={{ margin: 0 }}>{l.batchNo}</Tag>
                                </CopyValue>
                                <ExpiryChip date={l.expDate} />
                                {l.batchStatus && l.batchStatus !== 'AVAILABLE' && <Tag tone="gold">{human(l.batchStatus).toLowerCase()}</Tag>}
                              </div>
                            ) : (
                              <Tooltip label="The source picks the batch when it packs or dispatches">
                                <span className="type-caption text-subtle">not picked yet</span>
                              </Tooltip>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right align-middle">
                            <div className="type-body-strong text-content tabular-nums">{fmtQty(l.qty)}</div>
                            {phrase && <div className="type-caption text-subtle">{phrase}</div>}
                          </td>
                          {anyCounted && (
                            <td className="px-3 py-2.5 text-right align-middle tabular-nums">
                              {good == null ? <span className="type-caption text-subtle">—</span>
                                : <span className="type-body-strong text-brand-700 dark:text-brand-400">{fmtQty(good)}</span>}
                            </td>
                          )}
                          {anyCounted && (
                            <td className="px-3 py-2.5 text-right align-middle tabular-nums">
                              {!damaged ? <span className="type-caption text-subtle">—</span>
                                : <span className="type-body-strong text-harvest-700 dark:text-harvest-300">{fmtQty(damaged)}</span>}
                            </td>
                          )}
                          {anyCounted && (
                            <td className="px-3 py-2.5 text-right align-middle tabular-nums">
                              {!short ? <span className="type-caption text-subtle">—</span>
                                : <span className="type-body-strong text-danger-600 dark:text-danger-400">{fmtQty(short)}</span>}
                            </td>
                          )}
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
              </div>
            )}
          </Group>

          {/* ── DIFFERENCES ── one card each, because a difference is an unfinished conversation
              between two warehouses and an admin, not a table row. */}
          {data && discrepancies.length > 0 && (
            <Group icon={<AlertTriangle className="size-3.5" />} tint="bg-danger-500/10 text-danger-600 dark:bg-danger-500/15 dark:text-danger-400"
              title="Differences on receipt"
              hint={openDiffs ? `${openDiffs} still open` : 'all settled'}>
              <div className="space-y-2.5">
                {discrepancies.map((d: any) => (
                  <div key={d.id} className={cn('rounded-card border p-3.5',
                    d.status === 'OPEN'
                      ? 'border-danger-500/30 bg-danger-500/[0.06]'
                      : 'border-line bg-surface-2')}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Tag tone={d.status === 'OPEN' ? 'red' : 'neutral'}>{human(d.type)}</Tag>
                      <span className="type-body-strong text-content">{d.productName ?? `#${d.productId}`}</span>
                      <Tag tone={d.status === 'OPEN' ? 'gold' : 'green'}>
                        {d.status === 'OPEN' ? 'awaiting an admin decision' : 'settled'}
                      </Tag>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                      <F label="Expected">{fmtQty(d.expectedQty)}</F>
                      <F label="Received good">{fmtQty(d.receivedQty)}</F>
                      <F label="Damaged">{fmtQty(d.damagedQty)}</F>
                      <F label="Short">{fmtQty(d.shortQty)}</F>
                    </dl>
                    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                      <F label="Reported by"><Actor act={d.reported} /></F>
                      <F label={d.status === 'OPEN' ? 'Decision' : 'Settled by'}>
                        {d.status === 'OPEN'
                          ? <span className="font-normal text-subtle">Not taken yet — settle it from the register row.</span>
                          : (
                            <div className="space-y-1.5">
                              <Tag tone={d.resolution === 'RETURN_TO_SOURCE' ? 'blue' : 'neutral'}>
                                {d.resolution === 'RETURN_TO_SOURCE'
                                  ? 'Returned to source'
                                  : d.resolution === 'ACCEPT_LOSS' ? 'Written off as a loss' : human(d.resolution)}
                              </Tag>
                              {/* What the ruling DID, in stock terms. Without it the ledger entry the
                                  ruling produces reads as goods bouncing back to the source, rather
                                  than as the correction of a dispatch that overstated what left. */}
                              {d.resolution && (
                                <div className="type-caption font-normal text-subtle">
                                  {d.resolution === 'RETURN_TO_SOURCE'
                                    ? `${fmtQty(Number(d.shortQty) + Number(d.damagedQty))} put back on the source's books — the ruling was that it never left.`
                                    : 'Nothing was posted — the source lost it at dispatch, so the loss already stood.'}
                                </div>
                              )}
                              <Actor act={d.resolved} />
                            </div>
                          )}
                      </F>
                    </dl>
                    {(d.notes || d.resolutionNotes) && (
                      <div className="mt-3 space-y-1.5">
                        {d.notes && <div className="type-caption text-subtle"><span className="type-caption-strong text-content">Receipt note: </span>{d.notes}</div>}
                        {d.resolutionNotes && <div className="type-caption text-subtle"><span className="type-caption-strong text-content">Decision note: </span>{d.resolutionNotes}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Group>
          )}

          {/* ── THE LEDGER ── what the STOCK did, as opposed to what the workflow says. The two can
              disagree, and when they do this is the half that is true. */}
          {data && movements.length > 0 && (
            <Group icon={<ScrollText className="size-3.5" />} tint={SECTION_TINT.system} title="Stock movements posted"
              hint={`${movementGroups.length} ${movementGroups.length === 1 ? 'movement' : 'movements'}${
                movements.length !== movementGroups.length ? ` · ${movements.length} ledger rows` : ''}`}>
              <div className="overflow-x-auto rounded-card border border-line">
                <table className="w-full min-w-[680px] border-collapse">
                  <thead>
                    <tr className="bg-surface-2 text-left">
                      <th className="px-3 py-2 type-overline text-subtle">When</th>
                      <th className="px-3 py-2 type-overline text-subtle">Movement</th>
                      <th className="px-3 py-2 type-overline text-subtle">Warehouse</th>
                      <th className="px-3 py-2 type-overline text-subtle">Product</th>
                      <th className="px-3 py-2 type-overline text-subtle">Batch</th>
                      <th className="px-3 py-2 text-right type-overline text-subtle">Qty</th>
                      <th className="px-3 py-2 type-overline text-subtle">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* One product per group, its lots stacked beside it — the same shape the items
                        table uses, because it is the same fact seen from the ledger's side. */}
                    {movementGroups.map((g, gi) => g.rows.map((m: any, ri: number) => (
                      <tr key={String(m.id ?? `${g.key}:${ri}`)} className={cn(
                        // A hairline between lots of one movement; a heavier rule where the next
                        // movement starts, so the groups separate without a second colour.
                        ri > 0 && 'border-t border-line/70',
                        ri === 0 && gi > 0 && 'border-t-2 border-line-strong',
                        ri === 0 && gi === 0 && 'border-t border-line/70',
                      )}>
                        {ri === 0 && (
                          <td rowSpan={g.rows.length} className="whitespace-nowrap px-3 py-2 align-middle type-caption text-subtle">{g.when}</td>
                        )}
                        {ri === 0 && (
                          <td rowSpan={g.rows.length} className="px-3 py-2 align-middle">
                            <Tag tone={LEDGER_TONE[g.head.txnType] ?? 'neutral'}>{human(g.head.txnType)}</Tag>
                            {ledgerMeaning(g.head) && (
                              <div className="mt-1 max-w-[15rem] type-caption text-subtle">{ledgerMeaning(g.head)}</div>
                            )}
                          </td>
                        )}
                        {ri === 0 && (
                          <td rowSpan={g.rows.length} className="px-3 py-2 align-middle">
                            <div className="flex min-w-0 items-center gap-1.5">
                              {g.head.warehouseCode ? <WarehouseCode code={g.head.warehouseCode} /> : null}
                              <span className="truncate type-caption text-subtle">{g.head.warehouseName ?? `#${g.head.warehouseId}`}</span>
                            </div>
                          </td>
                        )}
                        {ri === 0 && (
                          <td rowSpan={g.rows.length} className="border-r border-line bg-surface-2/40 px-3 py-2 align-middle">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <ProductThumb name={g.head.productName ?? String(g.head.productId)} src={productImage(prodMap?.get(String(g.head.productId)))} size={28} />
                              <div className="min-w-0 truncate type-label-strong text-content">{g.head.productName ?? `#${g.head.productId}`}</div>
                            </div>
                            {/* The movement's own total earns its place only when there is more than
                                one lot to add up — on a single lot it is the row's figure reprinted. */}
                            {g.rows.length > 1 && (
                              <div className="mt-1.5 type-caption text-subtle">
                                {g.rows.length} lots · <b className="text-content"><LedgerQty qty={g.total} /></b>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 align-middle">
                          {m.batchNo ? (
                            <CopyValue value={m.batchNo} label="Batch">
                              <Tag tone="neutral" icon={<Package className="size-3" />} style={{ margin: 0 }}>{m.batchNo}</Tag>
                            </CopyValue>
                          ) : <span className="type-caption text-subtle">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right align-middle"><LedgerQty qty={m.qty} /></td>
                        {ri === 0 && (
                          <td rowSpan={g.rows.length} className="px-3 py-2 align-middle type-caption text-subtle">{g.head.actorName ?? 'System'}</td>
                        )}
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 type-caption text-subtle">
                Stock leaves the source on dispatch and lands at the destination through the goods receipt, so a
                completed transfer posts one entry out and one in per batch. A cancellation or a “return to source”
                decision posts its own credit back.
              </p>
            </Group>
          )}
        </div>
      )}
    </Modal>
  );
}

